import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/format.js';
import { PageHeader } from '../../patterns/PageHeader.js';
import { DataTable, type Column } from '../../patterns/DataTable.js';
import { Badge } from '../../ui/Badge.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card.js';
import { DetailList } from '../../ui/DetailList.js';
import { Spinner } from '../../ui/Spinner.js';
import { ApiError } from '../../lib/api.js';
import type { DataSummary, PurgeResult } from '../../lib/api.js';
import { AccountFlagsCard } from '../flags/AccountFlagsCard.js';
import { PurgeDialog } from './PurgeDialog.js';

/**
 * One account, and the only place its data can be destroyed.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE NUMBERS ON THIS PAGE COME FROM THE PLATFORM, NOT FROM THE LIST
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The tenant list already knows this account's name and event count, and
 * passing them through the click would save a request. It would also mean the
 * confirmation before a purge described whatever the list happened to be
 * showing — which, on a page left open while somebody else worked, is a
 * screen confidently naming the wrong account.
 *
 * So both the account and its row counts are re-fetched here, and the purge
 * confirmation is checked AGAIN by the platform against the slug it holds. A
 * console that has drifted cannot confirm its way past that.
 */
export function TenantPage({ id }: { id: string }) {
  const { api, me } = useSession();
  const tenant = useApiData(() => api.tenant(id), [api, id]);
  const summary = useApiData(() => api.dataSummary(id), [api, id]);
  const reset = useApiData(() => api.resetStatus(id), [api, id]);
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState<PurgeResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  /*
    The reset's OWN failure, not shared with the purge's.

    They were one `failure` until the unused-message detector pointed out that
    `tenant.resetFailedTitle` was never rendered — which was true, and the
    reason was that a failed reset was being titled "The purge did not run".
    Two operations, two outcomes, two sentences.
  */
  const [resetFailure, setResetFailure] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<number | null>(null);

  if (tenant.loading) {
    return (
      <div className="flex items-center gap-3 p-8 text-secondary">
        <Spinner />
        <span className="text-md">{t('common.loading')}</span>
      </div>
    );
  }

  if (tenant.error || !tenant.data) {
    return (
      <Banner intent="danger" title={t('shell.couldNotLoad')}>
        {tenant.error ?? t('tenant.missing')}
      </Banner>
    );
  }

  const account = tenant.data;

  /*
    Only `admin` and above may purge, and the button is ABSENT rather than
    disabled for anybody else.

    A disabled control invites the question "why not"; an absent one does not
    claim the action exists. The platform refuses regardless — the role is
    checked in the database — so this is about what the screen says, not about
    what it prevents.
  */
  const mayPurge = me?.role === 'admin' || me?.role === 'super_admin';

  const groupColumns: Column<DataSummary['groups'][number]>[] = [
    {
      id: 'group',
      header: t('tenant.dataGroup'),
      sort: (g) => g.group,
      cell: (g) => <span className="font-medium text-primary">{g.group}</span>,
    },
    {
      id: 'tables',
      header: t('tenant.dataTables'),
      align: 'right',
      nowrap: true,
      sort: (g) => g.tables,
      cell: (g) => formatNumber(g.tables),
    },
    {
      id: 'rows',
      header: t('tenant.dataRows'),
      align: 'right',
      nowrap: true,
      sort: (g) => g.rows,
      cell: (g) => formatNumber(g.rows),
    },
  ];

  return (
    <>
      <PageHeader
        title={account.name}
        description={account.slug}
        status={
          <Badge intent={account.status === 'active' ? 'success' : 'neutral'}>
            {account.status}
          </Badge>
        }
      />

      <div className="flex flex-col gap-6">
        {/*
          THE RESET PREFLIGHT: A REASON, NEVER A DISABLED BUTTON.

          Three states, and each says which of the two things is true — is this
          account a fixture, and may you act on it. A disabled control with no
          explanation makes an operator guess between them, and the two need
          completely different responses: one is "wrong account", the other is
          "ask somebody else".

          Rendered only for a demo account. On a real one the whole card is
          absent rather than present-and-refusing: the reset is not a thing that
          could apply here, and a permanently negative control is furniture.
        */}
        {reset.data?.resettable ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('tenant.resetTitle')}</CardTitle>
              <CardDescription>{t('tenant.resetBody')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                <p className="text-md text-secondary">
                  {reset.data.events > 0
                    ? t('tenant.resetReady', { events: formatNumber(reset.data.events) })
                    : t('tenant.resetEmpty')}
                </p>
                {resetFailure ? (
                  <Banner intent="danger" title={t('tenant.resetFailedTitle')}>
                    {resetFailure}
                  </Banner>
                ) : null}
                {resetDone !== null ? (
                  <Banner intent="success" title={t('tenant.resetDoneTitle')}>
                    {t('tenant.resetDoneBody', {
                      rows: formatNumber(resetDone),
                      name: account.name,
                    })}
                  </Banner>
                ) : null}
                {mayPurge ? (
                  <div>
                    <Button
                      variant="secondary"
                      disabled={resetting}
                      onClick={() => {
                        setResetFailure(null);
                        setResetting(true);
                        void api
                          .resetTenant(account.id)
                          .then((r) => {
                            setResetDone(r.totalRows);
                            /*
                              All three, because all three are now wrong: the
                              summary obviously, the account's event count on
                              the header above, and the preflight's own count.
                            */
                            void summary.reload();
                            void tenant.reload();
                            void reset.reload();
                          })
                          .catch((err: unknown) => {
                            setResetFailure(
                              err instanceof ApiError ? err.message : t('tenant.purgeFailedUnknown'),
                            );
                          })
                          .finally(() => setResetting(false));
                      }}
                    >
                      {resetting ? t('tenant.resetting') : t('tenant.resetAction')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-tertiary">{t('tenant.resetNoRole')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{t('tenant.aboutTitle')}</CardTitle>
            <CardDescription>{t('tenant.aboutBody')}</CardDescription>
          </CardHeader>
          <CardContent>
            <DetailList
              items={[
                { label: t('tenant.plan'), value: account.planCode ?? t('common.noValue') },
                {
                  label: t('tenant.modules'),
                  /*
                    Joined with a comma rather than rendered as fifteen badges.
                    A wall of chips is a wall; the question this answers is
                    "which modules", and a sentence answers it faster.

                    `layout: 'stacked'`, because this is the one row here that is prose
                    length. A default row puts the value right-aligned and
                    `shrink-0`, which does not wrap.
                  */
                  value:
                    account.entitlements.length > 0
                      ? account.entitlements.join(', ')
                      : t('common.none'),
                  layout: 'stacked',
                },
                { label: t('tenant.members'), value: formatNumber(account.members) },
                { label: t('tenant.created'), value: formatDate(account.createdAt) },
                {
                  label: t('tenant.lastActivity'),
                  value: account.lastEventAt
                    ? formatDateTime(account.lastEventAt)
                    : t('tenants.noActivity'),
                },
                {
                  label: t('tenant.demoResettable'),
                  value: account.demoResettable ? t('tenant.demoYes') : t('tenant.demoNo'),
                  hint: t('tenant.demoHint'),
                },
              ]}
            />
          </CardContent>
        </Card>

        {result ? (
          <Banner intent="success" title={t('tenant.purgedTitle')}>
            {t('tenant.purgedBody', {
              slug: result.slug,
              rows: formatNumber(result.totalRows),
              actor: result.actor,
            })}
          </Banner>
        ) : null}
        {failure ? (
          <Banner intent="danger" title={t('tenant.purgeRefusedTitle')}>
            {failure}
          </Banner>
        ) : null}

        <DataTable
          title={t('tenant.dataTitle')}
          description={t('tenant.dataBody')}
          columns={groupColumns}
          rows={summary.data?.groups ?? []}
          rowKey={(g) => g.group}
          loading={summary.loading}
          error={summary.error}
          onRetry={summary.reload}
          emptyReason="nothing-yet"
          emptyTitle={t('tenant.dataEmptyTitle')}
          emptyDescription={t('tenant.dataEmptyBody')}
          actions={
            mayPurge && summary.data ? (
              <Button variant="danger" onClick={() => setPurging(true)}>
                {t('tenant.purgeAction')}
              </Button>
            ) : null
          }
          footer={
            summary.data ? (
              <div className="flex flex-col gap-2">
                {/*
                  On a REAL account, say so where the reset card would have
                  been. Without this the absence of a reset reads as a missing
                  feature rather than as the platform refusing an operation
                  that does not apply.
                */}
                {reset.data && !reset.data.resettable ? (
                  <p className="text-sm text-tertiary">{t('tenant.resetNotDemo')}</p>
                ) : null}
                <p className="text-sm text-secondary">
                  {t('tenant.dataTotal', {
                    rows: formatNumber(summary.data.totalRows),
                    events: formatNumber(summary.data.events),
                  })}
                </p>
                {/*
                  Unclassified tables are NAMED, not counted.

                  The platform's coverage test fails loudly when a new
                  tenant-scoped table is added without being classified, so
                  this list should always be empty. If it is not, whoever is
                  looking at this screen needs to know which table — "3
                  unclassified" gives them nothing to act on.
                */}
                {summary.data.unclassified.length > 0 ? (
                  <Banner intent="warning" title={t('tenant.unclassifiedTitle')}>
                    {t('tenant.unclassifiedBody', {
                      tables: summary.data.unclassified.join(', '),
                    })}
                  </Banner>
                ) : null}
              </div>
            ) : null
          }
        />

        {/*
          Which features this account can see.

          On the account page rather than only on the rollout screen, because
          the question people arrive with is about ONE account — usually while
          on a call about it — and answering it should not mean finding the
          account's id and searching for it somewhere else.
        */}
        <AccountFlagsCard tenantId={id} />
      </div>

      {purging && summary.data ? (
        <PurgeDialog
          account={account}
          summary={summary.data}
          onCancel={() => setPurging(false)}
          onConfirm={async (confirmSlug) => {
            setFailure(null);
            try {
              const purged = await api.purge(account.id, confirmSlug);
              setResult(purged);
              setPurging(false);
              /*
                Both reloaded, because both are now wrong.

                The summary obviously. The account too: its event count is on
                the header of the page somebody is still looking at, and
                leaving it showing 118,760 rows after emptying the account is
                the kind of stale number that makes people re-run a
                destructive action.
              */
              void summary.reload();
              void tenant.reload();
            } catch (err) {
              /*
                A 409 is the platform refusing the confirmation, and its
                message names the account it actually holds. That is the most
                useful sentence available and it is passed through rather than
                replaced with a generic one.
              */
              setFailure(err instanceof ApiError ? err.message : t('tenant.purgeFailedUnknown'));
              setPurging(false);
            }
          }}
        />
      ) : null}
    </>
  );
}
